import { useCallback, useEffect, useRef, useState } from 'react';

// Remember a scroll container's position across unmounts, keyed by a stable
// view name. Tab switches unmount the whole page component (CategoryView),
// so the store lives at MODULE level — the same pattern as the nav-stack
// scroll restore (DJPalTab/FeedTab): component refs die exactly when the
// user comes back.
//
// Usage: const scrollRef = useScrollMemory('likes', [items.length]);
//        <div className="sidebar-content" ref={scrollRef}>
//
// deps: pass values that change as async content arrives (list lengths).
// Content usually isn't tall enough to restore into on the mounting commit —
// each deps change retries until the saved offset fits (clamped once the
// list has settled shorter than it was).
//
// opts.active: pass false while the container is showing a DIFFERENT view
// than the one the key describes (e.g. the playlists scroller swaps between
// the list and an expanded playlist) — saving pauses so the other view's
// offsets never pollute the key.
const _scrollByKey = new Map();

export function useScrollMemory(key, deps = [], { active = true } = {}) {
    // A CALLBACK ref, not a plain one: the container is usually conditional
    // (a collapsed sidebar, a closed panel), so it attaches on a later render
    // than the one that ran these effects. With `useRef` both effects saw
    // `null`, bailed, and — nothing in their deps having changed since —
    // never ran again: no listener, no restore, scroll silently forgotten.
    const [node, setNode] = useState(null);
    const ref = useCallback((el) => setNode(el), []);
    const restoredRef = useRef(false);
    const restoredNodeRef = useRef(null);
    const wasActiveRef = useRef(active);

    // Save continuously via a passive listener — reading scrollTop at
    // unmount is unreliable (the node may already be detached). Gated until
    // the restore has run so the browser's initial 0 (or a loading
    // skeleton's clamp) never clobbers the saved spot.
    useEffect(() => {
        if (!node || !active) return undefined;
        const onScroll = () => {
            if (!restoredRef.current) return;
            _scrollByKey.set(key, node.scrollTop);
        };
        node.addEventListener('scroll', onScroll, { passive: true });
        return () => node.removeEventListener('scroll', onScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node, key, active]);

    useEffect(() => {
        // Restoring is per VISIT, not per mount of the calling component: the
        // component outlives its scroller (a sidebar collapses, or swaps the
        // list out for a detail view and back) and a stale "already restored"
        // flag is what made the return trip land at the top. Re-arm whenever
        // the container is a different node, or the view becomes the one this
        // key describes again.
        if (node !== restoredNodeRef.current || (active && !wasActiveRef.current)) {
            restoredRef.current = false;
        }
        restoredNodeRef.current = node;
        wasActiveRef.current = active;
        if (restoredRef.current || !active || !node) return;
        const saved = _scrollByKey.get(key) || 0;
        if (!saved) { restoredRef.current = true; return; }
        const apply = () => {
            if (restoredRef.current) return;
            const max = node.scrollHeight - node.clientHeight;
            if (max >= saved) {
                node.scrollTop = saved;
                restoredRef.current = true;
            } else if (max > 0 && deps.length && deps.every((d) => d)) {
                // Content settled but shorter than it was (items removed
                // since last visit) — clamp instead of waiting forever.
                node.scrollTop = max;
                restoredRef.current = true;
            }
        };
        // Twice per attempt: layout may not include the new rows on the
        // committing frame (same trap as the nav-stack restore).
        apply();
        requestAnimationFrame(apply);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node, key, active, ...deps]);

    return ref;
}
